import {
  Delete,
  Equalizer,
  FilterAlt,
  Insights,
  InsightsOutlined,
} from "@mui/icons-material";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Select,
  Tooltip,
} from "@mui/material";
import DrawerCM from "components/shared/Drawer";
import generateHeadCells from "helper/generate-head-cell";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  StyledGridIcons,
  StyledMenuItem,
  StyledStack,
} from "./DisplayScore.style";

import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";
import { useEffect } from "react";
import { PAGINATION_LIMIT } from "constants";
import {
  getEducationYearRequest,
  getStudentClassesRequest,
  getStudentScoresRequest,
} from "api/requests";
import useHttp from "hooks/use-http";
import { useCallback } from "react";
import LoadingCM from "components/shared/Loading";
import { useNavigate } from "react-router-dom";

const headCells = generateHeadCells("user_score");

export default function UserScores() {
  const navigate = useNavigate();

  // filter option state
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedEducationalYear, setSelectedEducationalYear] = useState("");

  const [educationalYears, setEducationalYears] = useState([]);

  const [studentClasses, setStudentClasses] = useState({
    data: null,
    count: 0,
  });

  const [scoresData, setScoresData] = useState({
    data: [{ content: "اطلاعاتی یافت نشد" }],
    count: 0,
  });

  const { loading, request, setLoading } = useHttp(false);

  // paginition state
  const [page, setPage] = useState(1);

  const { register, control, setValue } = useForm();

  const getScores = async (filter = true) => {
    setLoading(true);
    const query = {
      skip: (page - 1) * PAGINATION_LIMIT,
      limit: PAGINATION_LIMIT,
      educational_year: selectedEducationalYear?.value ?? null,
      class_id: selectedClass?.value ?? null,
    };

    const res = await request({
      url: getStudentScoresRequest,
      values: filter ? query : { skip: 0, limit: PAGINATION_LIMIT },
      isMulty: true,
    });

    if (!res.count) {
      return setScoresData({
        data: [{ content: "اطلاعاتی یافت نشد" }],
        count: 0,
      });
    } else {
      const transformedData = res.data.map((item) => ({
        id: item.id,
        className: item.score_model.classModel.title,
        courceName: item.score_model.classModel.course.title,
        teacherName: `${item.score_model.classModel.teacher.first_name} ${item.score_model.classModel.teacher.last_name}`,
        score: item.is_present ? item.score : "غایب",
        educational_year: item.score_model.educational_year ?? "ثبت نشده",
        date: item.score_model.date.string ?? "ثبت نشده",
      }));

      setScoresData({
        data: transformedData,
        count: res.count,
      });
    }
    setLoading(false);
  };

  const getClasses = useCallback(async () => {
    setLoading(true);
    const { data: res } = await request({
      url: getStudentClassesRequest,
    });
    if (res.count || res.data.length) {
      const transformedData = res.data.map((item) => ({
        id: item.id,
        name: item.title,
      }));

      setStudentClasses({
        data: transformedData,
        count: res.count,
      });
    }
    setLoading(false);
  }, []);

  const getEducationYear = useCallback(async () => {
    setLoading(true);
    const res = await request({
      url: getEducationYearRequest,
      isMulty: true,
    });
    if (res) {
      setEducationalYears(res);
      setSelectedEducationalYear({ value: res[res.length - 2] });
    }
    setLoading(false);
  }, []);

  const getWithFilter = () => {
    setPage(1);
    getScores();
  };

  const getWithoutFilter = () => {
    setPage(1);
    setSelectedEducationalYear(null);
    setSelectedClass(null);
    getScores(false);
  };

  useEffect(() => {
    getEducationYear();
    getScores();
    getClasses();
  }, [getClasses, getEducationYear, page]);

  return (
    <DrawerCM title=" نمرات کلاسی دانش آموز" $styledHeader>
      <Grid item md={3} xs={12} my={3}>
        <Button
          fullWidth
          variant="outlined"
          size="large"
          sx={{ display: "flex", justifyContent: "start" }}
          startIcon={<Equalizer />}
          onClick={() => navigate("/scores/chart")}
        >
          مشاهده نمودار نمرات کلاسی
        </Button>
      </Grid>
      {loading ? (
        <LoadingCM />
      ) : (
        <>
          <StyledStack>
            <Grid container>
              <Grid item sm={5} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select-educational-year"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select-educational-year">
                        انتخاب سال تحصیلی
                      </InputLabel>
                      <Select
                        id="educationalYear"
                        name="educationalYear"
                        label="انتخاب سال تحصیلی"
                        labelId="select-educational-year"
                        value={selectedEducationalYear?.value ?? ""}
                        {...register("educationalYear")}
                        onChange={(e) =>
                          setSelectedEducationalYear({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        {educationalYears.map((item, index) => {
                          return (
                            <StyledMenuItem key={index} value={item}>
                              {item + 1} - {item}
                            </StyledMenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={5} xs={12} p={0.5}>
                <Controller
                  control={control}
                  name="student_class"
                  defaultValue={""}
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="student_class">انتخاب کلاس</InputLabel>
                      <Select
                        id="student_class"
                        name="student_class"
                        label="انتخاب کلاس"
                        labelId="select-student_class"
                        value={selectedClass?.value || ""}
                        {...register("student_class")}
                        onChange={(e) =>
                          setSelectedClass({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        {studentClasses.count &&
                          studentClasses?.data?.map((item, index) => {
                            return (
                              <StyledMenuItem key={index} value={item.id}>
                                {item.name}
                              </StyledMenuItem>
                            );
                          })}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <StyledGridIcons item sm={2} xs={12} p={1}>
                <IconButton color="primary" onClick={getWithFilter}>
                  <Tooltip title="اعمال فیلتر" placement="top">
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
                <IconButton color="error" onClick={getWithoutFilter}>
                  <Tooltip title="حذف فیلتر" placement="top">
                    <Delete />
                  </Tooltip>
                </IconButton>
              </StyledGridIcons>
            </Grid>
          </StyledStack>
          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                rows={scoresData.data}
                page={page}
                headCells={headCells}
                colspan={scoresData.count === 0 ? "10" : ""}
                fontSize="12px"
                cellFontSize="12px"
              />
              {scoresData.data.length < 1 ? (
                ""
              ) : (
                <PaginationCM
                  handleChange={(_, val) => setPage(val)}
                  page={page}
                  count={scoresData.count}
                />
              )}
            </Grid>
          </Grid>
        </>
      )}
    </DrawerCM>
  );
}
